/**
 * Area pricing, as this app asks for it.
 *
 * Both answers come from the server and neither is worked out here: whether a
 * day is cheaper depends on other people's bookings, which this app never sees
 * (ADR 0009 in `docs/phase-3-zip-proximity-plan.md`). What arrives is a tier
 * per day, the times that earn it, and — for one chosen time — a signed tier to
 * hand back at checkout.
 */

import { ProximityTier, normalizeZip, parseArrival } from "@homero/pricing";

export type { ProximityTier };

export interface AreaDay {
  tier: ProximityTier;
  /** Minutes from midnight, inclusive. Where a fixed arrival earns the discount. */
  windows: Array<{ startMinutes: number; endMinutes: number }>;
}

export type AreaCalendar =
  | { status: "served"; days: Record<string, AreaDay>; clusterPercent: number | null; travelPercent: number | null }
  | { status: "refused"; message: string }
  | { status: "unreachable" };

export interface ProximityQuote {
  tier: ProximityTier;
  percent: number;
  token: string | null;
  /** When the token stops being honoured, in ms since the epoch. */
  expiresAt?: number | null;
}

const API = process.env.REACT_APP_API_URL;

/** The longest range the calendar endpoint answers for in one request. */
const CALENDAR_DAYS = 62;

/** Whether a value is a ZIP, by the same rule the API applies. */
export function isZip(value: string | null | undefined): boolean {
  return normalizeZip(value) !== null;
}

/** `YYYY-MM-DD`, `days` after `date`. */
function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/**
 * Which days near a ZIP are cheaper, or dearer, from `from` for as long as the
 * endpoint allows.
 *
 * A 400 is the service area saying no, and carries the sentence to show. Any
 * other failure is "could not check", which must never read as a refusal — the
 * customer can still book, and checkout prices the area itself.
 */
export async function fetchAreaCalendar(zip: string, from: string): Promise<AreaCalendar> {
  const params = new URLSearchParams({
    zip,
    from,
    to: addDays(from, CALENDAR_DAYS - 1),
  });
  let response: Response;
  try {
    response = await fetch(`${API}/pricing/proximity/dates?${params.toString()}`);
  } catch {
    return { status: "unreachable" };
  }
  if (response.status === 400) {
    const body = await response.json().catch(() => ({}));
    return {
      status: "refused",
      message: typeof body?.error === "string" ? body.error : `We don't clean in ${zip} yet.`,
    };
  }
  if (!response.ok) return { status: "unreachable" };

  // A 200 that is not the JSON we expect — a proxy's HTML page, a body cut off
  // mid-way — is "could not check", like any other failure.
  const body = await response.json().catch(() => null);
  if (!body || !Array.isArray(body.dates)) return { status: "unreachable" };
  const days: Record<string, AreaDay> = {};
  for (const day of body.dates ?? []) {
    days[day.date] = { tier: day.tier, windows: day.windows ?? [] };
  }
  return {
    status: "served",
    days,
    clusterPercent: body.clusterPercent ?? null,
    travelPercent: body.travelPercent ?? null,
  };
}

/**
 * The signed tier for one arrival. Always asked as a one-time booking: the
 * frequency is chosen on the step after the date, and the cart decides whether
 * the tier applies from whatever frequency is chosen by then.
 */
export async function fetchProximityQuote(
  zip: string,
  serviceDate: string,
  cleanTime: string
): Promise<ProximityQuote> {
  const params = new URLSearchParams({
    zip,
    service_date: serviceDate,
    clean_time: cleanTime,
  });
  const response = await fetch(`${API}/pricing/proximity/quote?${params.toString()}`);
  if (!response.ok) throw new Error(`Proximity quote failed: ${response.status}`);
  const body = await response.json();
  return {
    tier: body.tier === "cluster" || body.tier === "travel" ? body.tier : "none",
    percent: Number(body.percent) || 0,
    token: typeof body.token === "string" ? body.token : null,
    expiresAt: typeof body.expiresAt === "string" ? Date.parse(body.expiresAt) || null : null,
  };
}

/**
 * Whether an arrival earns a day's nearby saving: Flexible Time always does, a
 * fixed time inside one of the day's windows does. Read with the server's own
 * `parseArrival`, so the time a saving is printed on is the time it is charged.
 */
export function arrivalEarns(arrival: string, windows: AreaDay["windows"]): boolean {
  const parsed = parseArrival(arrival);
  if (!parsed) return false;
  if (parsed.kind === "flexible") return true;
  return windows.some((w) => parsed.minutes >= w.startMinutes && parsed.minutes <= w.endMinutes);
}

/**
 * The `clean_time` a booking posts, from the store: a Same-Day window's label,
 * "flexible", or the fixed time. The quote has to ask about exactly this value,
 * because checkout matches the token against it.
 */
export function arrivalFor(state: {
  sameDay?: boolean;
  arrivalWindowLabel?: string;
  arrivalWindow?: string;
  isFlexible?: boolean;
  cleanTime?: string;
}): string {
  if (state.sameDay) return state.arrivalWindowLabel || state.arrivalWindow || "";
  if (state.isFlexible) return "flexible";
  return state.cleanTime || "";
}

type QuoteState = Parameters<typeof arrivalFor>[0] & { zip?: string; cleanDate?: string };

/**
 * The question a quote answers for this state — ZIP, date and arrival — or ""
 * when it cannot be asked yet. A tier held for any other question is stale.
 */
export function quoteKey(state: QuoteState): string {
  const arrival = arrivalFor(state);
  return isZip(state.zip) && state.cleanDate && arrival
    ? `${state.zip}|${state.cleanDate}|${arrival}`
    : "";
}

/** This state's quote, tagged with the question it answers; null if none can be asked. */
export async function quoteFor(
  state: QuoteState
): Promise<(ProximityQuote & { key: string }) | null> {
  const key = quoteKey(state);
  if (!key) return null;
  const quote = await fetchProximityQuote(state.zip!, state.cleanDate!, arrivalFor(state));
  return { ...quote, key };
}
